/**
 * client/src/lib/session-possession.ts — the possession heartbeat, client half
 * (ledger #119).
 *
 * The server refuses a gated read with 403 + `POSSESSION_PROOF_REQUIRED` when
 * this session cannot show a recent, correctly-bound WebAuthn assertion. This
 * module runs the ceremony that satisfies it: begin → `startLocalFirstAuthentication` →
 * complete. `apiRequest` (queryClient.ts) calls it and retries the original
 * request once.
 *
 * NO PRF HANDLING, DELIBERATELY. The server issues this challenge without the
 * PRF extension (see `server/logic/webauthn/session-possession/ceremony.ts`),
 * so no key material is produced here and there is nothing to `atomicWipe`
 * (L0-9 by construction, the `prf-reassertion.ts` posture inverted).
 *
 * ASKS BEFORE IT PROMPTS, when a UI is mounted to ask with. The heartbeat is
 * triggered by a READ, not by anything the holder did, so an unannounced
 * browser passkey sheet mid-read is the exact shape of a prompt people learn to
 * dismiss — or worse, learn to tap through without reading, which is the habit
 * every passkey-adjacent phishing lure depends on. `registerPossessionConfirmation`
 * lets a surface interpose an explanation; without one registered the ceremony
 * runs immediately, exactly as before, so no caller depends on the UI existing.
 *
 * SINGLE-FLIGHT. A screen typically fires several gated reads at once, and all
 * of them 403 together. Without dedupe each would open its own passkey prompt —
 * on most platforms the second `navigator.credentials.get()` aborts the first,
 * so the user would face a prompt storm that cannot succeed. `inFlight` makes
 * every concurrent caller await ONE ceremony.
 */

import type {
  PublicKeyCredentialRequestOptionsJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/browser";
import { startLocalFirstAuthentication } from "@/lib/webauthn/local-credential";
import {
  POSSESSION_PROOF_BEGIN_PATH,
  POSSESSION_PROOF_COMPLETE_PATH,
} from "@shared/possession-proof";

interface PossessionBeginResponse {
  options: PublicKeyCredentialRequestOptionsJSON;
  pinnedToSessionCredential: boolean;
}

/**
 * Dispatched on `window` around the ceremony so UI can explain the prompt
 * instead of letting the browser's passkey sheet appear unannounced. Listening
 * is optional — the ceremony does not depend on anyone handling these.
 */
export const POSSESSION_PROOF_EVENT = "strenn:possession-proof" as const;
export type PossessionProofPhase = "started" | "succeeded" | "failed";

function emit(phase: PossessionProofPhase): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(POSSESSION_PROOF_EVENT, { detail: { phase } }));
}

/**
 * Asks the holder to confirm before the passkey sheet opens. Resolves to
 * continue, rejects to abandon the ceremony.
 *
 * Registered by `SessionPossessionPrompt` (components/session-possession-prompt.tsx),
 * mounted once at the app root. Kept as a REGISTRATION rather than a direct
 * import so this module stays free of React and of any particular surface: the
 * ceremony is driven from `apiRequest`, which has no component tree to reach
 * into, and a headless caller (a test, a future non-React surface) still gets a
 * working heartbeat.
 */
export type PossessionConfirmationHandler = () => Promise<void>;

let confirmationHandler: PossessionConfirmationHandler | null = null;

/**
 * Interpose `handler` before every subsequent ceremony's passkey prompt.
 * Returns an unregister function; it only clears the handler if it is still the
 * registered one, so an unmount racing a later mount cannot blank the new one.
 */
export function registerPossessionConfirmation(
  handler: PossessionConfirmationHandler,
): () => void {
  confirmationHandler = handler;
  return () => {
    if (confirmationHandler === handler) confirmationHandler = null;
  };
}

/** Test seam — drops any registered handler. */
export function resetPossessionConfirmation(): void {
  confirmationHandler = null;
}

let inFlight: Promise<void> | null = null;

/**
 * Run the heartbeat. Resolves when the session is stamped; rejects if the
 * holder cancels the prompt or the assertion is refused.
 *
 * Concurrent callers share one ceremony. The `inFlight` slot is cleared in a
 * `finally` so a FAILED ceremony (holder dismissed the sheet) does not wedge
 * every later caller onto a permanently rejected promise.
 */
export function proveSessionPossession(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = runCeremony().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function runCeremony(): Promise<void> {
  // BEFORE `emit("started")`: the confirmation step is the holder deciding
  // whether a ceremony happens at all, so a surface listening for "started" to
  // render a busy state must not see one while the dialog is still open. A
  // rejection here (the holder chose "Not now") propagates unchanged and never
  // reaches the network — no challenge is minted for a prompt nobody answered.
  if (confirmationHandler) await confirmationHandler();

  emit("started");
  try {
    // Deliberately NOT `apiRequest`: these two calls must never re-enter the
    // 403 retry path in queryClient.ts. Both carry `requireAuth` plus a
    // floor-1 ladder row (`backlog.auth_webauthn_possession`, which can only
    // refuse with non-retriable `posture_unavailable`/`rollout_mode_invalid`)
    // and can never answer POSSESSION_PROOF_REQUIRED, but routing them through the
    // interceptor would make that a matter of the server never regressing
    // rather than a matter of structure.
    const begin = await postRaw<PossessionBeginResponse>(POSSESSION_PROOF_BEGIN_PATH, {});
    const response: AuthenticationResponseJSON = await startLocalFirstAuthentication(
      begin.options,
    );
    await postRaw(POSSESSION_PROOF_COMPLETE_PATH, { response });
    emit("succeeded");
  } catch (err) {
    emit("failed");
    throw err;
  }
}

/** Minimal POST that bypasses the interceptor. Throws on any non-2xx. */
async function postRaw<T = unknown>(url: string, data: unknown): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const msg = (body as Record<string, string> | null)?.error ?? res.statusText;
    throw new Error(`possession proof failed: ${msg}`);
  }
  return body as T;
}
